



































































































































































































































































































import gql from 'graphql-tag';
import Component from 'vue-class-component';

import { Import, Maybe, SystemJob, SystemJobProgress, SystemJobStep } from '@/api-svc-types';
import { BaseVue } from '@/BaseVue';
import UiButton from '@/components/ui/UiButton.vue';
import UiDataTable from '@/components/ui/UiDataTable.vue';
import UiLoading from '@/components/ui/UiLoading.vue';
import UiModal from '@/components/ui/UiModal.vue';
import UiPageHeader from '@/components/ui/UiPageHeader.vue';
import UiTooltip from '@/components/ui/UiTooltip.vue';
import { stringifyError } from '@/utils/error';

import RunJob from '../components/systemJobs/RunJob.vue';
import UiSelect from '../components/ui/UiSelect.vue';

//  generated api-svc-types predate the stepList and errors fields; extend locally until
//  regenerated
type JobRow = SystemJob & {
  stepList?: Maybe<Array<Maybe<SystemJobStep & { progress?: Maybe<SystemJobProgress> }>>>;
  errors?: Maybe<Array<Maybe<string>>>;
  inlineResults?: Maybe<string>;
  importSource?: Import;
};

//  a failed run that was already retried carries a retriedAsJobRunId marker in
//  inlineResults (see api-svc systemJobRetryMarker.ts); the server also rejects a second
//  retry, this just hides the button
function alreadyRetried(job: JobRow): boolean {
  if (!job.inlineResults) {
    return false;
  }
  try {
    return typeof JSON.parse(job.inlineResults)?.retriedAsJobRunId === 'string';
  } catch {
    return false;
  }
}

//  mirrors RETRYABLE_ACTIONS in api-svc bulkTransactionActionJobDefinition.ts; the name
//  set covers runs created before the action param was persisted
const RETRYABLE_ACTIONS = new Set([
  'delete',
  'markReconciled',
  'markUnreconciled',
  'uncategorize',
  'ignore',
  'unignore',
  'reconcile',
]);
const RETRYABLE_JOB_NAMES = new Set([
  'Delete Transactions',
  'Mark Transactions as Reconciled',
  'Unreconcile Transactions',
  'Uncategorize Transactions',
  'Ignore Transactions',
  'Unignore Transactions',
  'Reconcile Transactions',
]);

@Component({
  components: {
    UiDataTable,
    UiSelect,
    RunJob,
    UiLoading,
    UiTooltip,
    UiButton,
    UiModal,
    UiPageHeader,
  },
  apollo: {
    getSystemJobs: {
      query: gql`
        query SystemJobs($orgId: ID!, $limit: Int, $paginationOption: String) {
          getSystemJobs(orgId: $orgId, limit: $limit, paginationOption: $paginationOption) {
            nextPageToken
            items {
              type
              id
              createdByUser {
                userId
                name
                email
              }
              cancelledByUser {
                userId
                name
                email
              }
              createdSEC
              statusUpdatedSEC
              startedSEC
              completedSEC
              status
              stepList {
                id
                order
                status
                description
                startedSEC
                completedSEC
                successMessage
                errors
                progress {
                  numerator
                  denominator
                  units
                }
              }
              progress {
                numerator
                denominator
                units
              }
              name
              params {
                name
                value
              }
              errors
              inlineResults
            }
          }
        }
      `,
      variables() {
        return {
          orgId: this.$store.state.currentOrg.id,
          limit: typeof this.limit === 'string' ? Number(this.limit) : this.limit,
          paginationOption: this.paginationToken,
        };
      },
      result(result) {
        if (result.data.getSystemJobs) {
          this.systemJobsData = result.data.getSystemJobs.items;
          const pageToken = result.data.getSystemJobs.nextPageToken;
          console.log(pageToken);
          const split = pageToken.split('||');
          if (split.length === 2) {
            this.pagination.erlierPageToken = split[1];
            this.pagination.laterPageToken = split[0];
          }

          if (this.systemJobsData.length < this.limit) {
            this.earlierDisabled = true;
          } else {
            this.earlierDisabled = false;
          }
        }
      },
      loadingKey: 'isLoading',
      errorPolicy: 'ignore',
      //  running-job progress and auto-retried runs update without a manual refresh;
      //  the sibling imports query on this page already polls at 3s
      pollInterval: 10000,
    },
    imports: {
      query: gql`
        query imports($orgId: ID!, $importType: String) {
          imports(orgId: $orgId, importType: $importType) {
            id
            status
            createdMS
            importErrors
            progress
            createdByUser {
              userId
              name
              email
            }
          }
        }
      `,
      variables() {
        return {
          orgId: this.$store.state.currentOrg.id,
          importType: 'categorize',
        };
      },
      loadingKey: 'isLoading',
      pollInterval: 3000,
    },
  },
})
export default class SystemJobs extends BaseVue {
  pagination = {
    page: 1,
    erlierPageToken: '',
    laterPageToken: '',
  };

  //  constants
  readonly headers = [
    {
      id: 'jobId',
      label: 'Job Id',
      defaultVisibility: true,
    },
    {
      id: 'name',
      label: 'Job Name',
      defaultVisibility: true,
    },
    {
      id: 'status',
      label: 'Status',
      defaultVisibility: true,
    },
    {
      id: 'createdBy',
      label: 'Created By',
      defaultVisibility: true,
    },
    {
      id: 'createdSEC',
      label: 'Created On',
      defaultVisibility: true,
    },
    {
      id: 'params',
      label: 'Parameters',
      defaultVisibility: true,
    },
    {
      id: 'startedSEC',
      label: 'Started On',
      defaultVisibility: false,
    },
    {
      id: 'completedSEC',
      label: 'Completed On',
      defaultVisibility: false,
    },
    {
      id: 'actions',
      label: 'Actions',
      defaultVisibility: true,
    },
  ];

  // readonly pageSizes = pageSizes;
  isLoading = 0;
  importResultsView: Import | null = null;
  retryTarget: JobRow | null = null;
  retryLoading = false;

  //  Pagination
  paginationToken = '';
  earlierDisabled = false;
  laterDisabled = true;

  hasMore = false;
  limit = '10';

  // data
  systemJobsData = [] as SystemJob[];
  imports = [] as Import[];

  public refresh() {
    this.$apollo.queries.getSystemJobs.refetch();
  }

  public earlier() {
    if (!this.earlierDisabled) {
      this.pagination.page++;
      this.laterDisabled = false;
      this.paginationToken = `earlier||${this.pagination.erlierPageToken}`;
      console.log(this.paginationToken);
    }
  }

  public later() {
    if (!this.laterDisabled) {
      this.pagination.page--;
      if (this.pagination.page === 1) {
        this.laterDisabled = true;
      } else {
        this.laterDisabled = false;
      }
      this.earlierDisabled = false;
      this.paginationToken = `later||${this.pagination.laterPageToken}`;
      console.log(this.paginationToken);
    }
  }

  public getWalletName(walletId: string): Maybe<string> | undefined {
    const walletsArray = this.$store.state.wallets.wallets;
    const wallet = walletsArray?.find((w) => w.id === walletId);
    if (wallet) {
      return wallet.name;
    }

    return walletId;
  }

  public getErrors(errors: Maybe<string>[] | null | undefined): string {
    const list = (errors ?? []).filter((e): e is string => !!e);
    if (list.length > 0) {
      return `Errors:\n${list.join('\n')}`;
    }
    return 'Error';
  }

  //  run-level errors are written by the failure paths; step errors cover runs that
  //  failed before that fix shipped. Capped: the tooltip cannot scroll, so an unbounded
  //  error list would render past the viewport.
  public getFailureText(job: JobRow): string {
    const stepErrors = (job.stepList ?? []).flatMap((step) => step?.errors ?? []);
    const all = Array.from(new Set([...(job.errors ?? []), ...stepErrors].filter((e): e is string => !!e)));
    if (all.length === 0) {
      return 'Failed';
    }
    const shown = all.slice(0, 5).map((e) => (e.length > 200 ? `${e.slice(0, 200)}…` : e));
    const more = all.length - shown.length;
    return `Failed:\n${shown.join('\n')}${more > 0 ? `\n(+${more} more)` : ''}`;
  }

  public getProgressText(job: JobRow): string {
    const steps = (job.stepList ?? []).filter((step) => step && (step.progress?.numerator ?? 0) > 0);
    //  prefer the step that is actually running; a multi-step job would otherwise
    //  surface a finished step's count forever
    const step = steps.find((sp) => sp?.status === 'running') ?? steps[steps.length - 1];
    const progress: Maybe<SystemJobProgress> | undefined = step?.progress;
    if (!progress) {
      return '';
    }
    const units = progress.units ?? '';
    return `${(progress.numerator ?? 0).toLocaleString()} ${units}`.trim();
  }

  public canRetry(job: JobRow): boolean {
    if (job.importSource || job.status !== 'failed' || !this.checkScope(this.scopeLiterals.SystemJobsCreate)) {
      return false;
    }
    if (alreadyRetried(job)) {
      return false;
    }
    const actionParam = (job.params ?? []).find((p) => p?.name === 'action')?.value;
    if (actionParam) {
      return RETRYABLE_ACTIONS.has(actionParam);
    }
    return RETRYABLE_JOB_NAMES.has(job.name ?? '');
  }

  public async confirmRetry() {
    if (!this.retryTarget || this.retryLoading) {
      return;
    }
    this.retryLoading = true;
    try {
      await this.$apollo.mutate({
        mutation: gql`
          mutation retrySystemJob($orgId: ID!, $jobRunId: String!) {
            retrySystemJob(orgId: $orgId, jobRunId: $jobRunId)
          }
        `,
        variables: {
          orgId: this.$store.state.currentOrg.id,
          jobRunId: this.retryTarget.id,
        },
      });
      this.showSuccessSnackbar('Retry started - a new job run was created.');
      this.retryTarget = null;
      this.refresh();
    } catch (e) {
      const message = stringifyError(e, { hideErrorName: true }).replace('GraphQL error: ', '');
      this.showErrorSnackbar(`Error retrying job - ${message}`);
    } finally {
      this.retryLoading = false;
    }
  }

  public get combinedEntries(): any[] {
    const categorizeJobs = this.imports.map(
      (x) =>
        ({
          id: x.id,
          createdSEC: (x.createdMS ?? 0) / 1000,
          name: 'Categorize Transactions',
          params: [],
          status: x.status,
          createdByUser: x.createdByUser,
          importSource: x,
        } as SystemJob)
    );
    return this.systemJobsData.concat(categorizeJobs).sort((a, b) => {
      return (b.createdSEC ?? 0) - (a.createdSEC ?? 0);
    });
  }
}
